import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Check, X, Trash2, ShieldCheck, Clock, Crown } from "lucide-react";

export default function AccessAdmin() {
    const { admin: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [error, setError] = useState("");

    const load = useCallback(
        () => api.get("/admin/users").then((r) => setUsers(r.data)),
        []
    );
    useEffect(() => {
        load();
    }, [load]);

    const act = async (fn) => {
        setError("");
        try {
            await fn();
            await load();
        } catch (err) {
            setError(apiError(err.response?.data?.detail));
        }
    };

    const approve = (u) => act(() => api.post(`/admin/users/${u.user_id}/approve`));
    const revoke = (u) =>
        act(() => api.post(`/admin/users/${u.user_id}/revoke`));
    const remove = (u) => {
        if (!window.confirm(`Remove ${u.email}? They will lose access.`)) return;
        act(() => api.delete(`/admin/users/${u.user_id}`));
    };

    const pending = users.filter((u) => u.status !== "approved");
    const approved = users.filter((u) => u.status === "approved");

    const Row = ({ u }) => (
        <div
            data-testid={`access-row-${u.email}`}
            className="flex items-center gap-4 rounded-2xl border border-blue-500/15 bg-[#0c172e] p-4"
        >
            <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-blue-500/15 font-serif text-sky-300 ring-1 ring-blue-500/30">
                {u.picture ? (
                    <img src={u.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                    (u.name || u.email)[0].toUpperCase()
                )}
            </span>
            <div className="flex-1">
                <p className="flex items-center gap-2 font-serif text-lg text-slate-300">
                    {u.name || "—"}
                    {u.is_owner && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-amber-300">
                            <Crown className="h-3 w-3" /> Owner
                        </span>
                    )}
                    {u.email === me?.email && (
                        <span className="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-sky-300">
                            You
                        </span>
                    )}
                </p>
                <p className="text-xs text-slate-300/50">{u.email}</p>
            </div>
            {u.status === "approved" ? (
                <span className="flex items-center gap-1.5 text-xs text-emerald-300">
                    <ShieldCheck className="h-4 w-4" /> Approved
                </span>
            ) : (
                <span className="flex items-center gap-1.5 text-xs text-amber-300">
                    <Clock className="h-4 w-4" /> Pending
                </span>
            )}
            {u.status !== "approved" && (
                <button
                    data-testid={`access-approve-${u.email}`}
                    onClick={() => approve(u)}
                    aria-label="Approve"
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-emerald-300 hover:bg-emerald-500/15"
                    title="Approve"
                >
                    <Check className="h-4 w-4" />
                </button>
            )}
            {u.status === "approved" && !u.is_owner && u.email !== me?.email && (
                <button
                    onClick={() => revoke(u)}
                    aria-label="Revoke access"
                    data-testid={`access-revoke-${u.email}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-amber-300 hover:bg-amber-500/15"
                    title="Revoke access"
                >
                    <X className="h-4 w-4" />
                </button>
            )}
            {!u.is_owner && u.email !== me?.email && (
                <button
                    onClick={() => remove(u)}
                    aria-label="Remove"
                    data-testid={`access-remove-${u.email}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-red-300/80 hover:bg-red-500/15"
                    title="Remove"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    return (
        <div data-testid="admin-access">
            <h1 className="font-serif text-3xl text-blue-50">Access</h1>
            <p className="mt-1 text-sm text-slate-300/55">
                Approve who can manage the site. Share the private portal link, and approve people
                here after they sign in with Google.
            </p>
            {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

            {pending.length > 0 && (
                <>
                    <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-amber-300/80">
                        Waiting for approval ({pending.length})
                    </h2>
                    <div className="mt-3 space-y-3">
                        {pending.map((u) => (
                            <Row key={u.user_id} u={u} />
                        ))}
                    </div>
                </>
            )}

            <h2 className="mt-8 text-xs font-semibold uppercase tracking-wider text-slate-300/50">
                Team members ({approved.length})
            </h2>
            <div className="mt-3 space-y-3">
                {approved.map((u) => (
                    <Row key={u.user_id} u={u} />
                ))}
            </div>
        </div>
    );
}
